/**
 * The field mappings a Signify line item is written from: the org's configured
 * rules first, then the two fixed maps the integration owns, each inverted from
 * `Record<sourcePath, fieldId>` into a destination with one single-path rule.
 * Consumer: `useCreateLineItemFromSignify`.
 *
 * Precedence is destination-keyed and later wins, so a configuration-session
 * destination overrides an org rule and `REQUIRED_PRODUCT_ID_MAPPING` overrides
 * everything — the `product.code` -> `manufacturersProductId` pair is what the
 * duplicate lookup depends on, and no org may repoint it. `manufacturersSourceId`
 * is absent on purpose: the builder stamps `REQUIRED_SOURCE_ID_MAPPING` directly
 * rather than reading it from a response path.
 */
import {
  REQUIRED_PRODUCT_ID_MAPPING,
  SIGNIFY_CONFIGURATION_FIELD_MAPPINGS,
  type SignifyFieldMappings,
} from '#types';

const singlePathRules = (sourceKeyedMappings: Record<string, string>): SignifyFieldMappings =>
  Object.fromEntries(
    Object.entries(sourceKeyedMappings).map(([path, fieldId]) => [
      fieldId,
      [{ sources: [{ type: 'path' as const, path }] }],
    ])
  );

export const signifyLineItemFieldMappings = (
  fieldMappings: SignifyFieldMappings
): SignifyFieldMappings => ({
  ...fieldMappings,
  ...singlePathRules(SIGNIFY_CONFIGURATION_FIELD_MAPPINGS),
  ...singlePathRules(REQUIRED_PRODUCT_ID_MAPPING),
});
